import { handleActions } from "redux-actions";
import { SCOPE_BRAND_SET, PROMOTIONS, WORKING_ORDER } from "constants/action-types";
import { PROMOTIONS_LIST_KEY } from "../containers/admin/promotions/promotions-list";
import { PriceEntity } from "../containers/admin/promotions/entity/price-entity/promotions.types";
import { IPromotionsData } from "constants/promotion-types";
import { applyTrimOnPromoGroup } from "containers/admin/promotions/utils/helpers";
import _ from "lodash";
import type { IPromotionsListFilters } from "containers/admin/promotions/promotions-list/interfaces";
import type { SortState } from "ag-grid-community-32";

export interface INamespace {
    error?: Error;
    isFetching: boolean;
    results: PriceEntity[];
    cacheKey: string;
    nextPage?: string;
    hasMoreResults: boolean;
    filters?: IPromotionsListFilters;
    filteredResults?: PriceEntity[];
    sort?: SortState;
    shouldPersist?: boolean;
}

export interface IState {
    collections: {
        [namespace: string]: INamespace;
    };
}

export const initialState: IState = {
    collections: {},
};

const baseState: IPromotionsData = {
    currencyCode: null,
    unspecified: [],
    applied: [],
    threshold: [],
    totalApplied: 0,
};

interface IPromotionAction {
    type: string;
    payload: IPromotionsData;
}

export const orderPromotions = handleActions<IPromotionsData>(
    {
        [WORKING_ORDER.ORDER_PROMOTIONS.FETCH]: (state: IPromotionsData, action: IPromotionAction): IPromotionsData => {
            const appliedPromos = _.get(action, ["payload", "applied"], []);
            const thresholdPromos = _.get(action, ["payload", "threshold"], []);
            const orderAppliedPromos = appliedPromos.filter(({ productId }) => !productId);
            const orderThresholdPromos = thresholdPromos.filter(({ productId }) => !productId);
            const newPromotionData = applyTrimOnPromoGroup({
                ...baseState,
                ...action.payload,
                applied: orderAppliedPromos,
                threshold: orderThresholdPromos,
            });
            return {
                ...state,
                ...newPromotionData,
            };
        },
    } as any,
    baseState
);

export const getDefaultReducerState = (cacheKey?) => ({
    cacheKey,
    results: [],
    isFetching: true,
    hasMoreResults: false,
    nextPage: undefined,
    shouldPersist: false,
});

// TODO: invent type
const startFetchPromotions = (state: IState, { meta = {} }: any) => {
    if (meta.cacheKey && state.collections[meta.key] && state.collections[meta.key].cacheKey === meta.cacheKey) {
        return state;
    }
    if (meta.nextPage) {
        return {
            ...state,
            collections: {
                ...state.collections,
                [meta.key]: {
                    ...state.collections[meta.key],
                    isFetching: true,
                    hasMoreResults: false,
                    nextPage: undefined,
                },
            },
        };
    }
    if (state.collections[meta.key]?.shouldPersist) {
        return {
            ...state,
            collections: {
                ...state.collections,
                [meta.key]: {
                    ...getDefaultReducerState(meta.cacheKey),
                    sort: state.collections[meta.key]?.sort,
                    shouldPersist: false,
                },
            },
        };
    }
    return {
        ...state,
        collections: {
            ...state.collections,
            [meta.key]: getDefaultReducerState(meta.cacheKey),
        },
    };
};

export const promotionsReducer = handleActions<IState, any>(
    {
        [SCOPE_BRAND_SET]: () => {
            return initialState;
        },
        [PROMOTIONS.LIST.DELETE]: (state: IState, { payload }) => {
            const { id } = payload;
            const current = _.get(state, ["collections", PROMOTIONS_LIST_KEY, "results"], []);
            const filtered = current.filter((entity) => entity.id !== id);
            return _.set(state, ["collections", PROMOTIONS_LIST_KEY, "results"], filtered);
        },
        [PROMOTIONS.LIST.BULK_DELETE]: (state: IState, { payload }) => {
            const current = _.get(state, ["collections", PROMOTIONS_LIST_KEY, "results"], []);
            const filtered = current.filter((entity) => !payload.includes(entity.id));
            return _.set(state, ["collections", PROMOTIONS_LIST_KEY, "results"], filtered);
        },
        [PROMOTIONS.LIST.BULK_EDIT]: (state: IState, { payload }) => {
            const current = _.get(state, ["collections", PROMOTIONS_LIST_KEY, "results"], []);
            const updated = current.map((entity) => {
                const index = payload.results.findIndex((result) => entity.id === result.id);
                return index >= 0 ? payload.results[index] : entity;
            });

            return _.set(state, ["collections", PROMOTIONS_LIST_KEY, "results"], updated);
        },
        [PROMOTIONS.LIST.ADD]: (state: IState, { payload }) => {
            const current = _.get(state, ["collections", PROMOTIONS_LIST_KEY, "results"], []);

            return {
                ...state,
                collections: {
                    ...state.collections,
                    [PROMOTIONS_LIST_KEY]: {
                        results: [...current, payload],
                    },
                },
            };
        },
        [PROMOTIONS.LIST.FETCH.START]: startFetchPromotions,
        [PROMOTIONS.LIST.FETCH.SUCCESS]: (state: IState, { error, meta, payload }: any) => {
            const current = state.collections[meta.key];
            if (error) {
                return {
                    ...state,
                    collections: {
                        ...state.collections,
                        [meta.key]: {
                            ...current,
                            error: payload,
                            isFetching: false,
                            hasMoreResults: false,
                            nextPage: undefined,
                        },
                    },
                };
            }
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.key]: {
                        ...current,
                        results: meta.replace ? payload.results : (current?.results || []).concat(payload.results),
                        nextPage: payload.nextPage,
                        hasMoreResults: !!payload.nextPage,
                        isFetching: !!payload.nextPage,
                    },
                },
            };
        },
        // TODO: Clean with promotionsEnablesElasticSearchFilteringNu64701 FF
        [PROMOTIONS.LIST.SET_APPLY_FILTERS]: (state: IState, { payload }) => {
            const current = _.get(state, ["collections", PROMOTIONS_LIST_KEY, "results"], []);
            let filtered = current;

            Object.entries(payload.filters).forEach(([key, value]: [string, string[] | Date[]]) => {
                if (value.length === 0) {
                    delete payload.filters[key];
                    return;
                }

                switch (key) {
                    case "type": {
                        if (value.length == 1) {
                            filtered = filtered.filter((entity) => value[0] === entity.actions[0].price_action.scope);
                        }
                        break;
                    }
                    case "status": {
                        if (value.length == 1) {
                            filtered =
                                value[0] === "active"
                                    ? filtered.filter((entity) => entity.enabled === true)
                                    : filtered.filter((entity) => entity.enabled === false);
                        }
                        break;
                    }
                    case "dateRange": {
                        if (value.length == 2) {
                            filtered = filtered.filter((entity) => {
                                return (
                                    (entity.visible_start_date as string).split("T")[0] >=
                                        (value[0] as Date).toISOString().split("T")[0] &&
                                    (entity.visible_start_date as string).split("T")[0] <=
                                        (value[1] as Date).toISOString().split("T")[0]
                                );
                            });
                        }
                        break;
                    }
                    case "createdBy": {
                        filtered = filtered.filter((entity) => value.includes(entity.created_by));
                        break;
                    }
                    default: {
                        break;
                    }
                }
            });

            return {
                ...state,
                collections: {
                    ...state.collections,
                    [PROMOTIONS_LIST_KEY]: {
                        ...state.collections[PROMOTIONS_LIST_KEY],
                        filteredResults: filtered,
                        filters: payload.filters,
                    },
                },
            };
        },
        [PROMOTIONS.LIST.SET_FILTERS]: (state: IState, { payload }) => {
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [PROMOTIONS_LIST_KEY]: {
                        ...state.collections[PROMOTIONS_LIST_KEY],
                        filteredResults: payload.results,
                        filters: payload.filters,
                    },
                },
            };
        },
        [PROMOTIONS.LIST.SET_FETCHING]: (state: IState, { payload }) => {
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [PROMOTIONS_LIST_KEY]: {
                        ...state.collections[PROMOTIONS_LIST_KEY],
                        isFetching: payload.isFetching,
                    },
                },
            };
        },
        [PROMOTIONS.LIST.SET_SORT]: (state: IState, { payload }) => {
            return _.set(state, ["collections", PROMOTIONS_LIST_KEY, "sort"], payload.sort);
        },
        [PROMOTIONS.LIST.SET_PERSIST]: (state: IState, { payload }) => {
            if (state.collections?.[PROMOTIONS_LIST_KEY]?.results) {
                return _.set(state, ["collections", PROMOTIONS_LIST_KEY, "shouldPersist"], payload.shouldPersist);
            }
            return state;
        },
    },
    initialState
);
